import type { MessageAttachment } from '@hooks/useChat';

/**
 * Where an attachment's bytes come from. Pre-send the composer still holds the
 * raw `File`; once sent, only an auth-protected download URL survives.
 */
export type AttachmentSource =
  | { kind: 'local'; file: File }
  | { kind: 'remote'; url: string };

export interface PreviewableAttachment {
  key: string;
  filename: string;
  contentType: string;
  fileSize: number;
  /** `null` when neither a File nor a URL is available — renders as a static chip. */
  source: AttachmentSource | null;
  /**
   * A reduced copy to render from, where the server kept one. Rendering the
   * upload itself would cost megabytes of a scan to fill a hundred pixels of
   * tile, and put a file past the thumbnail budget that has a preview well
   * inside it.
   */
  preview?: {
    source: AttachmentSource;
    /** Absent for a shipped asset, whose size is only known once fetched. */
    fileSize?: number;
    /** Its extension picks the 3D loader; absent for a reduced image copy. */
    name?: string;
  };
}

/**
 * Identity for a pending file, tied to the `File` object rather than its
 * position or its metadata. An index-based key renumbers every later
 * attachment when an earlier one is removed, remounting their tiles and
 * dropping an open preview; name/size/date collides when the same file is
 * attached twice, which the composer allows.
 */
const fileKeys = new WeakMap<File, string>();
let lastFileKey = 0;

const keyForFile = (file: File): string => {
  const existing = fileKeys.get(file);
  if (existing) return existing;
  lastFileKey += 1;
  const key = `file-${lastFileKey}`;
  fileKeys.set(file, key);
  return key;
};

export const fromFiles = (files: File[]): PreviewableAttachment[] =>
  files.map((file) => ({
    key: keyForFile(file),
    filename: file.name,
    contentType: file.type,
    fileSize: file.size,
    source: { kind: 'local', file },
  }));

export const fromMessageAttachments = (
  attachments: MessageAttachment[],
): PreviewableAttachment[] =>
  attachments.map((att) => ({
    key: att.attachmentId,
    filename: att.filename,
    contentType: att.contentType,
    fileSize: att.fileSize,
    source: att.url ? { kind: 'remote', url: att.url } : null,
    preview:
      att.previewUrl && att.previewFileSize != null
        ? {
            source: { kind: 'remote', url: att.previewUrl },
            fileSize: att.previewFileSize,
            name: att.previewName ?? undefined,
          }
        : undefined,
  }));
